/**
 * Portal — employee-side gamification hooks.
 *
 * Consumes the `/v1/portal/*` routes under
 * `src/modules/portal/portal-gamification-*.controller.ts`. Auth is the
 * portal JWT (NOT the admin JWT) — requests must carry the PortalJwt token
 * that `portalAuthApi` stores.
 *
 * view-as hook uses the admin JWT (via `fetchApi` default) and is powered by
 * `src/modules/portal/portal-view-as.controller.ts`.
 */

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalAdminFetch } from '@/lib/portal/api';
import type {
  Badge,
  BadgePack,
  Challenge,
  DepartmentStats,
  EarnedBadge,
  PortalGamificationSettings,
  LeaderboardData,
  Level,
  PointsSummary,
  ActivityLogPage,
  PortalDashboardPayload,
} from '@/lib/types/gamification';

// ============================================================================
// Portal JWT fetch helper — re-exported from lib/api/portal-fetch.ts so
// use-portal-news + future portal hooks share the same auth path.
// ============================================================================

import { fetchPortalApi } from '@/lib/api/portal-fetch';

const fetchPortal = fetchPortalApi;

function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const qp = new URLSearchParams();
  for (const [k, v] of entries) qp.set(k, String(v));
  return `?${qp.toString()}`;
}

// ============================================================================
// Query keys
// ============================================================================

export const portalGamificationKeys = {
  all: ['portal-gamification'] as const,
  gamificationSettings: () =>
    [...portalGamificationKeys.all, 'gamification-settings'] as const,
  badgePacks: () => [...portalGamificationKeys.all, 'badge-packs'] as const,
  badges: {
    available: () => [...portalGamificationKeys.all, 'badges', 'available'] as const,
    earned: () => [...portalGamificationKeys.all, 'badges', 'earned'] as const,
    detail: (id: string) =>
      [...portalGamificationKeys.all, 'badges', id] as const,
  },
  leaderboard: (params?: { scope?: string; period?: string; limit?: number }) =>
    [...portalGamificationKeys.all, 'leaderboard', params ?? {}] as const,
  leaderboardDepartments: () =>
    [...portalGamificationKeys.all, 'leaderboard', 'departments'] as const,
  points: {
    history: (params?: { page?: number; limit?: number; activityType?: string }) =>
      [...portalGamificationKeys.all, 'points', 'history', params ?? {}] as const,
    summary: () => [...portalGamificationKeys.all, 'points', 'summary'] as const,
  },
  levels: {
    earned: () => [...portalGamificationKeys.all, 'levels', 'earned'] as const,
    ladder: () => [...portalGamificationKeys.all, 'levels', 'ladder'] as const,
  },
  challenges: {
    active: () => [...portalGamificationKeys.all, 'challenges', 'active'] as const,
    completed: () =>
      [...portalGamificationKeys.all, 'challenges', 'completed'] as const,
    detail: (id: string) =>
      [...portalGamificationKeys.all, 'challenges', id] as const,
  },
  viewAs: (employeeId: string) =>
    [...portalGamificationKeys.all, 'view-as', employeeId] as const,
};

// ============================================================================
// Gamification Settings (portal JWT — replaces admin useGamificationSettings)
// ============================================================================

export function usePortalGamificationSettings() {
  return useQuery({
    queryKey: portalGamificationKeys.gamificationSettings(),
    // The employee allow-list, not the admin settings row (audit G24, part).
    queryFn: () =>
      fetchPortal<PortalGamificationSettings | null>(`/portal/gamification-settings`),
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Badge Packs (portal JWT — replaces admin useBadgePacks)
// ============================================================================

/**
 * A pack from GET /v1/portal/badge-packs. `isEffective` marks the one pack
 * badges are awarded from — the tenant's copy once an admin customised a
 * built-in pack, which is never the pack marked default (audit G6).
 */
export type PortalBadgePack = BadgePack & { isEffective: boolean };

export function usePortalBadgePacks() {
  return useQuery({
    queryKey: portalGamificationKeys.badgePacks(),
    queryFn: () =>
      fetchPortal<PortalBadgePack[] | { data: PortalBadgePack[] }>(`/portal/badge-packs`).then(
        (r) => (Array.isArray(r) ? r : (r.data ?? [])),
      ),
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Badges
// ============================================================================

export function usePortalBadges() {
  return useQuery({
    queryKey: portalGamificationKeys.badges.available(),
    queryFn: () => fetchPortal<Badge[]>('/portal/badges/available'),
    placeholderData: keepPreviousData,
  });
}

export function usePortalEarnedBadges() {
  return useQuery({
    queryKey: portalGamificationKeys.badges.earned(),
    queryFn: () => fetchPortal<EarnedBadge[]>('/portal/badges/earned'),
    placeholderData: keepPreviousData,
  });
}

export function usePortalBadge(id: string) {
  return useQuery({
    queryKey: portalGamificationKeys.badges.detail(id),
    queryFn: () => fetchPortal<Badge>(`/portal/badges/${id}`),
    enabled: !!id,
  });
}

// ============================================================================
// Leaderboard
// ============================================================================

/**
 * The EMPLOYEE leaderboard (GET /v1/portal/leaderboard). Unlike the admin
 * leaderboard, a colleague's row carries no `email` — only the caller's own row
 * does — and with "Show leaderboard" off only the caller's own row is returned
 * (audit 2026-09-25, L11). Typed as such so no consumer assumes an address.
 */
export type PortalLeaderboardData = Omit<LeaderboardData, 'entries'> & {
  entries: Array<Omit<LeaderboardData['entries'][number], 'email'> & { email?: string }>;
};

export function usePortalLeaderboard(params?: {
  scope?: string;
  period?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: portalGamificationKeys.leaderboard(params),
    queryFn: () =>
      fetchPortal<PortalLeaderboardData>(
        `/portal/leaderboard${buildQuery(params as Record<string, unknown>)}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function usePortalDepartmentLeaderboard() {
  return useQuery({
    queryKey: portalGamificationKeys.leaderboardDepartments(),
    queryFn: () => fetchPortal<DepartmentStats[]>('/portal/leaderboard/departments'),
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Points
// ============================================================================

export function usePortalPointsHistory(params?: {
  page?: number;
  limit?: number;
  activityType?: string;
}) {
  return useQuery({
    queryKey: portalGamificationKeys.points.history(params),
    queryFn: () =>
      fetchPortal<ActivityLogPage>(
        `/portal/points/history${buildQuery(params as Record<string, unknown>)}`,
      ),
    placeholderData: keepPreviousData,
    select: (raw): ActivityLogPage => ({
      rows: raw.rows ?? [],
      total: raw.total ?? 0,
      page: raw.page ?? 1,
      limit: raw.limit ?? 25,
      totalPages: raw.totalPages ?? 1,
    }),
  });
}

export function usePortalPointsSummary() {
  return useQuery({
    queryKey: portalGamificationKeys.points.summary(),
    queryFn: () => fetchPortal<PointsSummary>('/portal/points/summary'),
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Levels
// ============================================================================

export function usePortalLevels() {
  return useQuery({
    queryKey: portalGamificationKeys.levels.earned(),
    queryFn: () => fetchPortal<Level | null>('/portal/levels/earned'),
    placeholderData: keepPreviousData,
  });
}

/**
 * The workspace's level ladder (GET /v1/portal/levels): the active levels the
 * current level is picked from, lowest threshold first — the portal roadmap.
 */
export function usePortalLevelLadder() {
  return useQuery({
    queryKey: portalGamificationKeys.levels.ladder(),
    queryFn: () => fetchPortal<Level[]>('/portal/levels'),
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Challenges
// ============================================================================

export function usePortalActiveChallenges() {
  return useQuery({
    queryKey: portalGamificationKeys.challenges.active(),
    queryFn: () => fetchPortal<Challenge[]>('/portal/challenges/active'),
    placeholderData: keepPreviousData,
  });
}

export function usePortalCompletedChallenges() {
  return useQuery({
    queryKey: portalGamificationKeys.challenges.completed(),
    queryFn: () => fetchPortal<Challenge[]>('/portal/challenges/completed'),
    placeholderData: keepPreviousData,
  });
}

export function usePortalChallenge(id: string) {
  return useQuery({
    queryKey: portalGamificationKeys.challenges.detail(id),
    queryFn: () => fetchPortal<Challenge>(`/portal/challenges/${id}`),
    enabled: !!id,
  });
}

export function useAcceptChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchPortal<void>(`/portal/challenges/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: portalGamificationKeys.challenges.active(),
      });
    },
  });
}

// ============================================================================
// Admin view-as (uses admin JWT, not portal JWT)
// ============================================================================

export function usePortalViewAs(employeeId: string) {
  return useQuery({
    queryKey: portalGamificationKeys.viewAs(employeeId),
    queryFn: () =>
      portalAdminFetch<PortalDashboardPayload>(`portal/view-as/${employeeId}`),
    enabled: !!employeeId,
  });
}
